import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { IoIosFlame } from "react-icons/io";
import { FaEgg, FaSun } from "react-icons/fa6";
import { MdOutlineWatchLater } from "react-icons/md";
import Image from '@/components/common/SpiritoryImage';
import { useRouter } from 'next/router';

import { SkuTrendingBadgeProps } from '@/types/sku';
import {
  END_OF_SUMMER_SALE_PATH,
  END_OF_SUMMER_SALE_QUERY_FLAG,
  END_OF_SUMMER_SALE_SKU_TAG,
  IS_EASTER_ENABLED,
  IS_END_OF_SUMMER_SALE_ENABLED,
} from '@/constants/campaigns.constants';
import { getActiveSkuTags } from '@/utilites/sku-tags';

export default function SkuTrendingBadge({
  sku,
  forceBlackFriday = false,
  forceEaster = false,
  forceEndOfSummerSale = false,
}: SkuTrendingBadgeProps) {
  const { t } = useTranslation('index');
  const router = useRouter();
  const isBlackFridayPage = router?.pathname === '/Black-Friday-2025';
  const isBlackFridayContext = forceBlackFriday || isBlackFridayPage || !!router?.query?.bf;

  const isEasterPage = router?.pathname === '/easter-sale-2026';
  const isEasterContext = IS_EASTER_ENABLED && (forceEaster || isEasterPage || !!router?.query?.easter);

  const skuTagsList = getActiveSkuTags(sku.sku_tags);

  const isEndOfSummerSalePage = router?.pathname === END_OF_SUMMER_SALE_PATH;
  const isEndOfSummerSaleContext =
    IS_END_OF_SUMMER_SALE_ENABLED &&
    (forceEndOfSummerSale ||
      isEndOfSummerSalePage ||
      !!router?.query?.[END_OF_SUMMER_SALE_QUERY_FLAG] ||
      skuTagsList.includes(END_OF_SUMMER_SALE_SKU_TAG));

  const baseClasses =
    'tw-flex tw-items-center tw-justify-center tw-rounded-full tw-px-[8px] tw-text-base tw-font-medium max-nine:tw-text-[10px] max-nine:tw-px-[3px] max-nine:tw-py-[2px] tw-whitespace-nowrap';

  // Black Friday context: force a black Black Friday badge for all bottles
  if (isBlackFridayContext) {
    return (
      <span className={clsx(baseClasses, 'tw-bg-black !tw-text-white')}>
        <span className={`max-nine:tw-flex max-nine:tw-items-center`}>
          <span className="max-nine:tw-hidden">Black Friday</span>
          <span className="tw-hidden max-nine:tw-inline">BF</span>
        </span>
      </span>
    );
  }

  // Easter context: force a soft green Easter Sale badge for all bottles
  if (isEasterContext) {
    return (
      <span className={clsx(baseClasses, 'tw-bg-[#E2F5E9] !tw-text-[#2E7D32] tw-py-[2px]')}>
        <span className={`tw-flex tw-items-center tw-gap-1`}>
          <FaEgg className="tw-text-[12px] max-nine:tw-text-[10px] !tw-text-[#e6b8a2]" />
          <span className="!tw-text-[12px] max-nine:!tw-text-[10px] tw-font-bold">
            <span className="max-nine:tw-hidden">{t('easter_sale_label')}</span>
            <span className="tw-hidden max-nine:tw-inline">Easter</span>
          </span>
        </span>
      </span>
    );
  }

  // End of Summer context: the campaign orange, on every bottle of the sale
  if (isEndOfSummerSaleContext) {
    return (
      <span className={clsx(baseClasses, 'tw-bg-[#F2724D] !tw-text-white tw-py-[2px]')}>
        <span className={`tw-flex tw-items-center tw-gap-1`}>
          <FaSun className="tw-text-[12px] max-nine:tw-text-[10px]" />
          <span className="!tw-text-[12px] max-nine:!tw-text-[10px] tw-font-bold">
            <span className="max-nine:tw-hidden">{t('end_of_summer_sale_label')}</span>
            <span className="tw-hidden max-nine:tw-inline">Sale</span>
          </span>
        </span>
      </span>
    );
  }

  if (skuTagsList.includes('preorder')) {
    return (
      <span className={clsx(baseClasses, 'tw-bg-[#2D6A4F] !tw-text-white')}>
        <span className={`tw-flex tw-items-center tw-gap-1`}>
          <MdOutlineWatchLater className="max-nine:tw-text-[18px]" />
          <span className="max-nine:tw-hidden">Pre-Order</span>
          <span className="tw-hidden max-nine:tw-inline">Pre</span>
        </span>
      </span>
    );
  }

  if (sku.hot_deal || skuTagsList.includes('hot_deal')) {
    return (
      <span className={clsx(baseClasses, 'tw-bg-[#C51F3E] !tw-text-white')}>
        <span className={`tw-flex tw-items-center tw-gap-1`}>
          <IoIosFlame className="max-nine:tw-text-[18px]" />
          <span className="max-nine:tw-hidden">{t('hot_deal_label')}</span>
        </span>
      </span>
    );
  }

  if (sku.rare_bottle) {
    return (
      <span className={clsx(baseClasses, 'tw-bg-main !tw-text-white')}>
        <span className="tw-flex tw-items-center tw-gap-1">
          <Image
            src={`https://dataspiritory.b-cdn.net/assets/images/spiritoryLogo_white.svg`}
            alt={``}
            width={14}
            height={14}
            className="max-nine:tw-w-[15px] max-nine:tw-h-[15px]"
          />
          <span className="max-nine:tw-hidden">{t('rare_bottle')}</span>
        </span>
      </span>
    );
  }

  return null;
}